'use client';

import 'BomdisoftComponents/inputs/date-range/styles.css';
import { Button } from 'BomdisoftComponents/buttons/index';
import { CalendarMonthRoundedIcon } from 'BomdisoftComponents/icons/calendar-month-rounded';
import { ChevronLeftIcon } from 'BomdisoftComponents/icons/chevron-left';
import { ChevronRightIcon } from 'BomdisoftComponents/icons/chevron-right';
import { KeyboardArrowDownRoundedIcon } from 'BomdisoftComponents/icons/keyboard-arrow-down-rounded';
import { addDays, addMonths, dayKey, isSameDay, isSelectable, monthGrid, startOfDay, weekStartFor } from 'BomdisoftComponents/inputs/date-range/range';
import { Menu } from 'BomdisoftComponents/menu/index';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { type ReactNode, useMemo, useRef, useState } from 'react';

export type DayPreset = { days: number; label: string };

export type DayPickerProps = {
  applyLabel: string;
  cancelLabel: string;
  label: string;
  locale: string;
  max: Date;
  min: Date;
  nextMonthLabel: string;
  onChange: (day: string) => void;
  presets: DayPreset[];
  previousMonthLabel: string;
  testId?: string;
  today: Date;
  triggerClassName?: string;
  value: string;
};

const dayOf = (key: string): Date => new Date(`${key}T00:00:00Z`);

export const DayPicker = ({ applyLabel, cancelLabel, label, locale, max, min, nextMonthLabel, onChange, presets, previousMonthLabel, testId = 'day-picker', today, triggerClassName, value }: DayPickerProps): ReactNode => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Date>(() => dayOf(value));
  const [view, setView] = useState<Date>(() => addMonths(dayOf(value), 0));
  const days = useMemo(() => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC', year: 'numeric' }), [locale]);
  const months = useMemo(() => new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC', year: 'numeric' }), [locale]);
  const weekdays = useMemo(() => new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'narrow' }), [locale]);
  const cellDay = useMemo(() => new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' }), [locale]);
  const cellFull = useMemo(() => new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeZone: 'UTC' }), [locale]);
  const weekStart = useMemo(() => weekStartFor(locale), [locale]);
  const weekdayNames = useMemo(() => Array.from({ length: 7 }, (_, index) => weekdays.format(new Date(Date.UTC(2024, 0, 7 + ((weekStart + index) % 7))))), [weekStart, weekdays]);
  const presetDay = (preset: DayPreset): string => dayKey(addDays(startOfDay(today), preset.days));

  const openPicker = () => {
    setPicked(dayOf(value));
    setView(addMonths(dayOf(value), 0));
    setOpen(true);
  };

  const choosePreset = (preset: DayPreset) => {
    onChange(presetDay(preset));
    setOpen(false);
  };

  const apply = () => {
    onChange(dayKey(picked));
    setOpen(false);
  };

  const canPageBack = addMonths(view, -1).getTime() >= addMonths(startOfDay(min), 0).getTime();
  const canPageForward = addMonths(view, 1).getTime() <= startOfDay(max).getTime();

  return (
    <>
      <button aria-expanded={open} aria-haspopup='dialog' className={triggerClassName ?? 'bomdisoft-date-range-trigger'} data-testid={testId} onClick={openPicker} ref={triggerRef} type='button'>
        <span aria-hidden='true' className='bomdisoft-date-range-trigger-icon'>
          <CalendarMonthRoundedIcon size={16} />
        </span>
        <span className='bomdisoft-date-range-trigger-text'>{days.format(dayOf(value))}</span>
        <span aria-hidden='true' className='bomdisoft-date-range-trigger-icon'>
          <KeyboardArrowDownRoundedIcon size={18} />
        </span>
      </button>

      <Menu anchorRef={triggerRef} className='bomdisoft-date-range-panel' label={label} onClose={() => setOpen(false)} open={open} role='dialog' testId={`${testId}-panel`}>
        <div className='bomdisoft-date-range-body'>
          {presets.length === 0 ? null : (
            <div className='bomdisoft-date-range-presets' data-testid={`${testId}-presets`}>
              {presets.map(preset => (
                <button className='bomdisoft-date-range-preset' data-selected={presetDay(preset) === value ? '' : undefined} data-testid={`${testId}-preset-${preset.days}`} key={preset.days} onClick={() => choosePreset(preset)} type='button'>
                  {preset.label}
                </button>
              ))}
            </div>
          )}

          <div className='bomdisoft-date-range-calendar'>
            <div className='bomdisoft-date-range-pager'>
              <Tooltip placement='top' title={previousMonthLabel}>
                <button aria-label={previousMonthLabel} className='bomdisoft-date-range-page' data-testid={`${testId}-previous`} disabled={!canPageBack} onClick={() => setView(addMonths(view, -1))} type='button'>
                  <ChevronLeftIcon size={18} />
                </button>
              </Tooltip>
              <span className='bomdisoft-date-range-spacer' />
              <Tooltip placement='top' title={nextMonthLabel}>
                <button aria-label={nextMonthLabel} className='bomdisoft-date-range-page' data-testid={`${testId}-next`} disabled={!canPageForward} onClick={() => setView(addMonths(view, 1))} type='button'>
                  <ChevronRightIcon size={18} />
                </button>
              </Tooltip>
            </div>
            <div className='bomdisoft-date-range-months'>
              <div className='bomdisoft-date-range-month'>
                <p className='bomdisoft-date-range-month-name'>{months.format(view)}</p>
                <div className='bomdisoft-date-range-weekdays'>
                  {weekdayNames.map((name, index) => (
                    <abbr key={`${name}-${index}`}>{name}</abbr>
                  ))}
                </div>
                <div className='bomdisoft-date-range-grid'>
                  {monthGrid(view, weekStart).map(day => (
                    <button
                      aria-label={cellFull.format(day)}
                      aria-pressed={isSameDay(day, picked)}
                      className='bomdisoft-date-range-day'
                      data-outside={day.getUTCMonth() !== view.getUTCMonth() ? '' : undefined}
                      data-position={isSameDay(day, picked) ? 'single' : 'outside'}
                      data-testid={day.getUTCMonth() === view.getUTCMonth() ? `${testId}-day-${dayKey(day)}` : undefined}
                      data-today={isSameDay(day, today) ? '' : undefined}
                      disabled={!isSelectable(day, min, max)}
                      key={dayKey(day)}
                      onClick={() => setPicked(day)}
                      type='button'
                    >
                      {cellDay.format(day)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='bomdisoft-date-range-footer'>
          <p className='bomdisoft-date-range-summary' data-testid={`${testId}-summary`}>
            {days.format(picked)}
          </p>
          <Button onClick={() => setOpen(false)} size='small' variant='text'>
            {cancelLabel}
          </Button>
          <Button data-testid={`${testId}-apply`} onClick={apply} size='small' variant='contained'>
            {applyLabel}
          </Button>
        </div>
      </Menu>
    </>
  );
};
